import { useEffect, useState } from 'react'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { supabase } from '@/lib/supabase'
import { Profile } from '@/lib/supabase'

export function useProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user) {
      setProfile(null)
      setIsLoading(false)
      return
    }

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single()

        if (error) throw error

        // Combine fetched data with user's email
        if (data && user?.email) {
          setProfile({ ...data, email: user.email })
        } else if (data) {
          // Handle case where user email might not be available but data is
          // This might indicate an issue if Profile type strictly requires email
          console.warn("User email not available, setting profile without it or handling as an error state.");
          // Depending on strictness, either set with partial data (if Profile.email is optional)
          // or set an error/null profile. Forcing it for now if Profile expects email.
          // To make it compile, we'd need to ensure email is part of data or make Profile.email optional
          // For now, this will still cause an error if Profile.email is mandatory and user.email is null/undefined
          // Let's assume Profile type expects email, and user.email should be available if user exists.
          // If user.email can be undefined, Profile type in lib/supabase.ts should mark email as optional.
          // For this fix, we'll proceed assuming user.email is available if user is.
          setError(new Error("Failed to fetch profile: User email missing"))
          setProfile(null)
        } else {
          setProfile(null) // No data from profiles table
        }
      } catch (error) {
        setError(error instanceof Error ? error : new Error('Failed to fetch profile'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchProfile()

    // Subscribe to changes
    const subscription = supabase
      .channel('profile_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            setProfile(null)
          } else {
            // Combine payload data with user's email
            if (payload.new && user?.email) {
              setProfile({ ...(payload.new as Omit<Profile, 'email'>), email: user.email })
            } else {
              console.warn("User email not available for profile subscription update or payload.new is missing.");
              // Optionally, handle this more gracefully, e.g., by not updating or logging
              // If payload.new is the source of truth from DB (and DB doesn't have email)
              // and Profile type *requires* email, this indicates a discrepancy.
            }
          }
        }
      )
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [user])

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user?.id) {
      console.error("User ID not available, cannot update profile.");
      // Optionally, you could throw an error or return a specific result:
      // throw new Error("User ID is not available to update profile.");
      return; // Or return Promise.reject(new Error("User ID missing"))
    }

    // Create a new object for updates to avoid mutating the original 'updates' argument
    // and explicitly exclude 'email' as it's managed by auth.users, not the profiles table.
    const { email, ...profileUpdates } = updates; 
    // Now 'profileUpdates' will not contain the 'email' property.

    try {
      // Update the profile in the profiles table
      const { data, error: supabaseError } = await supabase
        .from('profiles')
        .update(profileUpdates) // Use the sanitized profileUpdates
        .eq('id', user.id) // user.id is now guaranteed to be a string here

      if (supabaseError) {
        console.error("Detailed Supabase error from updateProfile:", supabaseError); // Log the specific Supabase error
        throw supabaseError; // Throw the original Supabase error
      }
      
      // If full_name is being updated, also update user metadata to keep it in sync
      if (updates.full_name !== undefined) {
        const { error: metadataError } = await supabase.auth.updateUser({
          data: { full_name: updates.full_name }
        })
        
        if (metadataError) {
          console.error('Error updating user metadata:', metadataError)
          // Continue anyway as the profile update succeeded
        }
      }
    } catch (error) { // This will now catch the specific supabaseError if thrown
      console.error("Error caught in updateProfile's catch block:", error); // Log the error that reached here
      throw error instanceof Error ? error : new Error('Failed to update profile')
    }
  }

  return {
    profile,
    isLoading,
    error,
    updateProfile,
  }
} 